import { useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { nonNullable } from 'lib/array/arrayUtils'
import type {
  CartPanelAccommodationItem,
  CartPanelExperienceItem,
  CartPanelFlightItem,
  CartPanelItem,
} from '../../hooks/useCartPanelItems'
import { isSavedFlightSearchExpired } from '../../utils/flightCartPanelUtils'

const META_SEPARATOR = ' · '

export interface ChatCartRowSummary {
  /** Secondary line under the item name, e.g. "Standard Double Room · 1 adult". */
  meta: string | undefined
  /**
   * Shown in place of a price when the item cannot be booked and the customer has
   * no action to take, e.g. a sold-out property.
   */
  statusLabel: string | undefined
}

function formatShortDate(date: string | undefined, locale: string): string | undefined {
  if (!date) return undefined
  return intlDateFormatter(date, INTL_DMY_CASUAL_SHORT_FORMAT, locale)
}

function getAccommodationMeta(item: CartPanelAccommodationItem, intl: IntlShape): Array<string | undefined> {
  const stayLabel = item.dates ?? (item.duration
    ? intl.formatMessage(
        {
          id: 'GQY0DKErdsrlLFeRV9Ax',
          defaultMessage: '{nights}-night stay',
          description: 'Night count shown under a hotel name when dates are missing but the trip duration is known',
        },
        { nights: item.duration },
      )
    : undefined)

  return [stayLabel, item.roomName, item.occupancy]
}

function getExperienceMeta(item: CartPanelExperienceItem, intl: IntlShape): Array<string | undefined> {
  const ticketCount = item.tickets.reduce((total, ticket) => total + ticket.count, 0)
  const ticketsLabel = ticketCount > 0
    ? intl.formatMessage(
        {
          id: 'yNA7HNQy4KAf8g-x7_Ie',
          defaultMessage: '{count, plural, one {# ticket} other {# tickets}}',
          description: 'Ticket count shown on a compact chat cart row for a booked experience',
        },
        { count: ticketCount },
      )
    : undefined

  return [formatShortDate(item.date, intl.locale), item.time, ticketsLabel, item.locationName]
}

function getFlightMeta(item: CartPanelFlightItem, intl: IntlShape): Array<string | undefined> {
  const departureDate = formatShortDate(
    item.savedItemData.departureDate ?? item.savedItemData.departureTime?.split('T')[0],
    intl.locale,
  )
  const returnDate = formatShortDate(
    item.returnSavedItemData?.departureDate ?? item.returnSavedItemData?.departureTime?.split('T')[0],
    intl.locale,
  )

  const dateLabel = departureDate && returnDate
    ? intl.formatMessage(
        {
          id: 'w76HLwVtPQi3ix9VSVv8',
          defaultMessage: '{departureDate} – {returnDate}',
          description: 'Outbound and return date range shown on a compact chat cart row for a return flight',
        },
        { departureDate, returnDate },
      )
    : departureDate

  const { departureAirport, arrivalAirport } = item.savedItemData
  const routeLabel = departureAirport && arrivalAirport
    ? intl.formatMessage(
        {
          id: 'bG_balwm3bvx2EHaHnGj',
          defaultMessage: '{departureAirport} to {arrivalAirport}',
          description: 'Airport pair shown on a compact chat cart row describing where a flight travels between',
        },
        { departureAirport, arrivalAirport },
      )
    : undefined

  return [dateLabel, routeLabel]
}

/**
 * True when the only thing wrong with a flight is that its price lapsed - either a
 * converted cart item whose fare expired, or a saved search that lapsed before the
 * traveller picked a fare. A withdrawn fare, past dates, or a refresh that already
 * failed are different problems and keep the plain "Unavailable" status.
 */
function isFlightPriceExpired(item: CartPanelFlightItem): boolean {
  const status = item.flightCartItem?.status
  if (status === 'dates_in_past' || status === 'unavailable') return false
  if (item.isAvailabilityBlocked) return !item.hasRefreshFailed
  return item.cartStatus === 'needs_flight_selection' && isSavedFlightSearchExpired(item)
}

function getStatusLabel(item: CartPanelItem, intl: IntlShape): string | undefined {
  if (item.isBooked) {
    return intl.formatMessage({
      id: 'sBdeT8C_ZvuptdNQTvEM',
      defaultMessage: 'Booked',
      description: 'Status shown on a compact chat cart row for an item the customer has already purchased',
    })
  }

  if (item.productType === 'flight' && isFlightPriceExpired(item)) {
    return intl.formatMessage({
      id: 'ldNI6dJo4OF3jwI_Pe1n',
      defaultMessage: 'Price expired',
      description: 'Status shown on a compact chat cart row when a flight price has expired and needs refreshing',
    })
  }

  switch (item.cartStatus) {
    case 'property_sold_out':
    case 'experience_sold_out':
      return intl.formatMessage({
        id: 'Xi5Z7F-uX_feczMlnGUa',
        defaultMessage: 'Sold out',
        description: 'Status shown on a compact chat cart row when the hotel or experience is sold out',
      })
    case 'selected_rate_unavailable':
    case 'duration_unavailable':
    case 'experience_unavailable':
      return intl.formatMessage({
        id: 'QZyyWZsSTfh9QOr8mfUq',
        defaultMessage: 'Unavailable',
        description: 'Status shown on a compact chat cart row when the selected rate or duration is no longer available',
      })
    default:
      return item.pricing.isAvailable === false
        ? intl.formatMessage({
            id: 'FoDCmJeCHXqt5j6di5mr',
            defaultMessage: 'Unavailable',
            description: 'Status shown on a compact chat cart row when live pricing reports the item cannot be booked',
          })
        : undefined
  }
}

function getMetaParts(item: CartPanelItem, intl: IntlShape): Array<string | undefined> {
  switch (item.productType) {
    case 'accommodation': return getAccommodationMeta(item, intl)
    case 'experience': return getExperienceMeta(item, intl)
    case 'flight': return getFlightMeta(item, intl)
  }
}

export function useChatCartRowSummary(item: CartPanelItem): ChatCartRowSummary {
  const intl = useIntl()

  return useMemo(() => {
    const meta = nonNullable(getMetaParts(item, intl)).join(META_SEPARATOR)

    return {
      meta: meta.length > 0 ? meta : undefined,
      statusLabel: getStatusLabel(item, intl),
    }
  }, [item, intl])
}
